import type { ReactNode } from 'react';
import { faNum as fa, formatRial, type ReconUnpriced as Unpriced } from '@kia/shared';
import { useApiResource } from '../lib/useApiResource';
import { Card, ErrorRetry } from '../ui';

/**
 * سن‌بندی دریافتنی/پرداختنی (AR/AP aging) — open receivables per customer and payables per supplier,
 * bucketed by age. Payments are applied FIFO to the oldest items. Read-only view (accounting.read).
 */

interface Buckets {
  current: number;
  d31_60: number;
  d61_90: number;
  over90: number;
  total: number;
}
interface Row {
  partyId: number | null;
  partyName: string;
  buckets: Buckets;
}
interface Report {
  rows: Row[];
  totals: Buckets;
}

/**
 * BIDX-P6 REPAIR — the AP total shrank; this is where it owns up.
 *
 * `computeApAging` excludes unpriced receipts from the payables it ages, and this screen is the one whose
 * whole purpose a human reads as «چقدر بدهکاریم و چقدر دیر شده». Every other total that moved in that
 * commit got its qualifier (the reconciliation worksheet caption, the dashboard tile hint, the inventory
 * footnote, the AI brief); this table rendered the smaller number under an unchanged label, seven
 * components above the only surface that mentioned the exclusion. The figure comes from the route, which
 * reads the SAME `unpricedReceipts` authority the worksheet does, so the two accounting surfaces cannot
 * name different amounts.
 *
 * IT RENDERS ON THE EMPTY BRANCH TOO, and that is the case that matters most: a book whose payables are
 * ALL unpriced shows an empty table, and «موردی نیست» alone would be a flat untruth.
 *
 * The «ثبت قیمت» action is gated on purchase.write while this screen is accounting.read, so the sentence
 * names the action and who performs it rather than instructing a Finance reader to press a button they
 * cannot see.
 */
function UnpricedApNote({ unpriced }: { unpriced: Unpriced | null }) {
  if (!unpriced || unpriced.count === 0) return null;
  return (
    <p style={{ margin: '6px 0 0', color: 'var(--ink-3)', fontSize: '.85em', lineHeight: 1.9 }}>
      <b>{formatRial(unpriced.rials)}</b> ریال روی <b>{fa(unpriced.count)}</b> رسید خرید با «قیمت
      تأییدنشده» در این جدول نیامده — قیمت پرداختی وارد نشده و از قیمت سفارش برآورد شده، پس پول
      تأییدشده نیست و از دفاتر کنار گذاشته شده است
      {unpriced.oldestDays > 0 ? <> (قدیمی‌ترین: {fa(unpriced.oldestDays)} روز)</> : null}. یعنی
      «پرداختنی» بالا تا این اندازه کمتر از واقع است. چکِ پرداخت‌شدهٔ همان سفارش‌ها هم کنار گذاشته
      شده، وگرنه بدهیِ دیگری را تسویه‌شده نشان می‌داد. تأیید با «ثبت قیمت» در «تاریخچهٔ خرید» انجام
      می‌شود؛ اگر دسترسی خرید ندارید، از واحد خرید بخواهید آن را ثبت کند.
    </p>
  );
}

function AgingTable({
  title,
  report,
  note,
}: {
  title: string;
  report: Report | null;
  note?: ReactNode;
}) {
  if (!report || report.rows.length === 0) {
    return (
      <div style={{ marginBottom: 12 }}>
        <b>{title}</b>
        <p style={{ color: 'var(--ink-3)', marginTop: 4 }}>موردی نیست.</p>
        {note}
      </div>
    );
  }
  const cell = (n: number) => (n ? formatRial(n) : '—');
  return (
    <div style={{ marginBottom: 12 }}>
      <b>{title}</b>
      <div style={{ overflowX: 'auto' }}>
        <table className="table" style={{ marginTop: 6 }}>
          <thead>
            <tr>
              <th>طرف</th>
              <th>جاری (۰–۳۰)</th>
              <th>۳۱–۶۰</th>
              <th>۶۱–۹۰</th>
              <th>بیش از ۹۰</th>
              <th>جمع</th>
            </tr>
          </thead>
          <tbody>
            {report.rows.map((r) => (
              <tr key={`${r.partyId}`}>
                <td>{r.partyName}</td>
                <td>{cell(r.buckets.current)}</td>
                <td>{cell(r.buckets.d31_60)}</td>
                <td>{cell(r.buckets.d61_90)}</td>
                <td style={{ color: r.buckets.over90 ? 'var(--red)' : undefined }}>
                  {cell(r.buckets.over90)}
                </td>
                <td>
                  <b>{formatRial(r.buckets.total)}</b>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>
                <b>جمع کل</b>
              </td>
              <td>{cell(report.totals.current)}</td>
              <td>{cell(report.totals.d31_60)}</td>
              <td>{cell(report.totals.d61_90)}</td>
              <td>{cell(report.totals.over90)}</td>
              <td>
                <b>{formatRial(report.totals.total)}</b>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      {note}
    </div>
  );
}

export function AgingPanel() {
  const res = useApiResource<{ ar: Report; ap: Report; unpriced: Unpriced }>(
    '/api/accounting/aging',
  );

  return (
    <Card title="سن‌بندی دریافتنی/پرداختنی">
      {res.status === 'error' ? (
        <ErrorRetry error={res.error} onRetry={res.reload} />
      ) : (
        <>
          <AgingTable title="دریافتنی از مشتریان (AR)" report={res.data?.ar ?? null} />
          <AgingTable
            title="پرداختنی به تأمین‌کنندگان (AP)"
            report={res.data?.ap ?? null}
            note={<UnpricedApNote unpriced={res.data?.unpriced ?? null} />}
          />
        </>
      )}
    </Card>
  );
}
